// License Management Constants
// Note: Enum values and names are now fetched from API via enumsApi
// These constants are kept for reference and backwards compatibility

// License Type Colors (mapped by value)
export const LicenseTypeColors = {
  0: 'bg-gray-100 text-gray-800',      // Free
  1: 'bg-blue-100 text-blue-800',      // Basic
  2: 'bg-primary-100 text-primary-800', // Premium
  3: 'bg-purple-100 text-purple-800',   // Enterprise
};

// Building Module Type Colors (mapped by value)
export const BuildingModuleTypeColors = {
  1: 'bg-pink-100 text-pink-700',      // Announcements
  2: 'bg-blue-100 text-blue-700',      // Amenities
  3: 'bg-teal-100 text-teal-700',      // Parking
  4: 'bg-indigo-100 text-indigo-700',  // Deliveries
  8: 'bg-orange-100 text-orange-700',  // Maintenance
  9: 'bg-purple-100 text-purple-700',  // Building Assistant
};

// Helper Functions for working with API enum data
// API returns: { value: number, name: string, displayName: string }

/**
 * Format enum array from API to { value, label } format for FormSelect
 * @param {Array} enumArray - Array from API with { value, name, displayName }
 * @returns {Array} - Array of { value, label } for dropdowns
 */
export const formatEnumForSelect = (enumArray) => {
  if (!enumArray || !Array.isArray(enumArray)) return [];
  return enumArray.map(item => ({
    value: item.value,
    label: item.displayName || item.name || `Unknown (${item.value})`,
  }));
};

/**
 * Get display name from enum value using API data
 * @param {number} value - Enum value
 * @param {Array} enumArray - Array from API with { value, name, displayName }
 * @returns {string} - Display name
 */
export const getEnumDisplayName = (value, enumArray) => {
  if (!enumArray || !Array.isArray(enumArray)) return 'Unknown';
  const item = enumArray.find(e => e.value === value);
  return item ? (item.displayName || item.name) : 'Unknown';
};

/**
 * Create a name lookup map from API enum data
 * @param {Array} enumArray - Array from API
 * @returns {Object} - Map of value -> displayName
 */
export const createEnumLookup = (enumArray) => {
  if (!enumArray || !Array.isArray(enumArray)) return {};
  return enumArray.reduce((acc, item) => {
    acc[item.value] = item.displayName || item.name;
    return acc;
  }, {});
};

// Legacy helper functions (kept for backwards compatibility)
// These will work with cached enum data

export const getLicenseTypeName = (type, licenseTypes = null) => {
  if (licenseTypes) {
    return getEnumDisplayName(type, licenseTypes);
  }
  // Fallback to hardcoded names if API data not available
  const names = { 0: 'Free', 1: 'Basic', 2: 'Premium', 3: 'Enterprise' };
  return names[type] || 'Unknown';
};

export const getLicenseTypeColor = (type) => {
  return LicenseTypeColors[type] || 'bg-gray-100 text-gray-800';
};

export const getModuleName = (module, moduleTypes = null) => {
  if (moduleTypes) {
    return getEnumDisplayName(module, moduleTypes);
  }
  // Fallback to hardcoded names if API data not available
  const names = {
    1: 'Announcements',
    2: 'Amenities',
    3: 'Parking',
    4: 'Deliveries',
    8: 'Maintenance',
    9: 'Building Assistant'
  };
  return names[module] || 'Unknown';
};

export const getModuleColor = (module) => {
  return BuildingModuleTypeColors[module] || 'bg-gray-100 text-gray-700';
};

export const getActivationMethodName = (method, activationMethods = null) => {
  if (activationMethods) {
    return getEnumDisplayName(method, activationMethods);
  }
  // Fallback to hardcoded names
  const names = { 0: 'Code Redemption', 1: 'Admin Assignment', 2: 'Auto Assignment' };
  return names[method] || 'Unknown';
};

export const formatLicenseCode = (code) => {
  if (!code) return '';
  // Format as XXXX-XXXX-XXXX-XXXX if not already formatted
  const clean = code.replace(/-/g, '');
  if (clean.length === 16) {
    return `${clean.slice(0, 4)}-${clean.slice(4, 8)}-${clean.slice(8, 12)}-${clean.slice(12, 16)}`;
  }
  return code;
};

export const isLicenseActive = (license) => {
  return license?.isActive === true;
};

export const isUserLicenseExpired = (userLicense) => {
  if (!userLicense?.expiresAt) return true;
  return new Date(userLicense.expiresAt) < new Date();
};

export const getLicenseStatusBadge = (isActive) => {
  return isActive
    ? { text: 'Active', color: 'bg-green-100 text-green-800' }
    : { text: 'Inactive', color: 'bg-gray-100 text-gray-800' };
};

export const getUserLicenseStatusBadge = (userLicense) => {
  if (isUserLicenseExpired(userLicense)) {
    return { text: 'Expired', color: 'bg-red-100 text-red-800' };
  }
  return { text: 'Active', color: 'bg-green-100 text-green-800' };
};

export const formatRedemptions = (current, max) => {
  if (max === null || max === undefined) {
    return `${current} / Unlimited`;
  }
  return `${current} / ${max}`;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// The local calendar day the date falls on — the same day toLocaleDateString prints —
// placed on the UTC line. Date.UTC has no DST, so the gap between two of these is always
// an exact number of days, where two local midnights would be 23 or 25 hours apart across
// a time change and leave a fraction behind.
const localCalendarDay = (date) =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

export const calculateDaysUntilExpiration = (expiresAt) => {
  if (!expiresAt) return null;
  const expireDate = new Date(expiresAt);
  const now = new Date();
  const days = (localCalendarDay(expireDate) - localCalendarDay(now)) / MS_PER_DAY;
  // A license that lapsed earlier today is still on today's calendar day, but it has
  // expired. Positive differences cannot be in the past, so this is the only correction.
  if (days === 0 && expireDate < now) return -1;
  return days;
};

export const formatDaysUntilExpiration = (expiresAt) => {
  const days = calculateDaysUntilExpiration(expiresAt);
  if (days === null) return 'N/A';
  if (days < 0) return 'Expired';
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `${days} days remaining`;
};

// New License System (per-building) Constants

export const LicenseStatusColors = {
  0: 'bg-green-100 text-green-800',     // Active
  1: 'bg-blue-100 text-blue-800',       // Trial
  2: 'bg-yellow-100 text-yellow-800',   // GracePeriod
  3: 'bg-red-100 text-red-800',         // Suspended
  4: 'bg-gray-100 text-gray-800',       // Expired
  5: 'bg-gray-100 text-gray-600',       // Cancelled
};

export const getLicenseStatusName = (status) => {
  const names = { 0: 'Active', 1: 'Trial', 2: 'Grace Period', 3: 'Suspended', 4: 'Expired', 5: 'Cancelled' };
  return names[status] ?? 'Unknown';
};

export const getLicenseStatusColor = (status) => {
  return LicenseStatusColors[status] || 'bg-gray-100 text-gray-800';
};

export const formatPlanLimits = (plan) => {
  const parts = [];
  if (plan.maxUsers != null) parts.push(`${plan.maxUsers} users`);
  if (plan.maxFloors != null) parts.push(`${plan.maxFloors} floors`);
  if (plan.maxUnits != null) parts.push(`${plan.maxUnits} units`);
  return parts.length > 0 ? parts.join(', ') : 'Unlimited';
};
